import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { CRM_TABS } from '@/Pages/tabRoutes';
import CustomersTab from './CustomersTab';
import SegmentsTab from './SegmentsTab';
import FeedbackTab from './FeedbackTab';
import CouponsTab from './CouponsTab';
import PromotionsTab from './PromotionsTab';
import type { Promotion } from './lib';


/**
 * P12-02 / P12-05 / P12-06 — CRM (§23). Segments are derived server-side, the
 * loyalty ledger is append-only, and service recovery is a status on the
 * feedback row rather than a second entity.
 */
export default function Customers() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/customers', CRM_TABS);

  // Promotions are shared: the coupon form links a code to one of them.
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loadingPromotions, setLoadingPromotions] = useState(true);

  const loadPromotions = useCallback(() => {
    setLoadingPromotions(true);
    api
      .get<{ data: Promotion[] }>('/promotions', { params: { limit: 100 } })
      .then(({ data }) => setPromotions(data.data ?? []))
      .catch(() => setPromotions([]))
      .finally(() => setLoadingPromotions(false));
  }, []);

  useEffect(loadPromotions, [loadPromotions]);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('nav.customers')}</h1>
        <p className="text-sm text-ink-muted">{t('crm.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((entry) => ({ key: entry.id, label: t(entry.labelKey) }))}
        value={current}
        onChange={select}
        label={t('nav.customers')}
        idPrefix="crm"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'customers' && <CustomersTab />}
      {current === 'segments' && <SegmentsTab />}
      {current === 'feedback' && <FeedbackTab />}
      {current === 'coupons' && <CouponsTab promotions={promotions} />}
      {current === 'promotions' && (
        <PromotionsTab
          promotions={promotions}
          loading={loadingPromotions}
          onChanged={loadPromotions}
        />
      )}
    </div>
  );
}
